import React, {
	useCallback,
	useEffect,
	useState,
	useRef,
	useContext,
} from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MailIcon from '@mui/icons-material/Mail';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MoreIcon from '@mui/icons-material/MoreVert';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { Button, Select, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { makeStyles } from '@mui/styles';
// import CityIcon from '../../img/megurimIcon.svg';
import debounce from 'lodash/debounce';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';

import { Marker, Popup } from 'react-leaflet';
import { latLng } from 'leaflet';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsIcon from '@mui/icons-material/Directions';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchBox from './SearchBox';
import { LayerContext } from '../context/LayerContext';

import MegurimIcon from '../../img/megurimIcon.svg';
import { searcher } from '../../helpers/searcher';

const Item = styled(Box)(({ theme }) => ({
	...theme.typography.body2,
	padding: theme.spacing(1),
	// textAlign: 'center',
	color: theme.palette.text.secondary,
}));

const AutoCompleteListItem = styled(Box)(({ theme }) => ({
	paddingLeft: '20px',
	'&:hover': {
		background: 'blue',
	},
}));

const useStyles = makeStyles({
	staticSearchIcon: {
		transform: 'rotate(45deg)',
	},
});

export default function MainAppBar() {
	const { map, setPoint, setActivePark } = useContext(LayerContext);
	const [anchorEl, setAnchorEl] = useState(null);
	const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
	const classes = useStyles();
	const [searchInput, setSearchInput] = useState('');
	const isMenuOpen = Boolean(anchorEl);
	const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
	const [options, setOptions] = useState([]);
	const [mapCenterObject, setMapCenterObject] = useState({});

	const lastValueSearchedRef = useRef('');
	const lastSearchedPointRef = useRef<any>({});
	const matches = useMediaQuery('(min-width:600px)');
	const [isSearchBoxFocused, setIsSearchBoxFocused] = useState(false);

	const getOptionsDelayed = useCallback(
		debounce((text: string, callback: any) => {
			setOptions([]);
			if (text.length > 2) {
				if (
					text !== lastValueSearchedRef.current && // dont send api request if current value aquals previous value
					text !== lastSearchedPointRef?.current?.formatted // dont send api request if current value aquals the
					// last given object.formatted. (in the onClick of the autocomplete input event it will fly to the last point )
				) {
					lastValueSearchedRef.current = text;
					searcher(text).then(callback);
				}
			}
		}, 250),
		[]
	);

	useEffect(() => {
		getOptionsDelayed(searchInput, (filteredOptions: any) => {
			// console.log(filteredOptions);
			setOptions(filteredOptions);
			// setLastValueSearched(searchInput);
		});
	}, [searchInput, getOptionsDelayed]);

	const handleProfileMenuOpen = (event: any) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMobileMenuClose = () => {
		setMobileMoreAnchorEl(null);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		handleMobileMenuClose();
	};

	const handleMobileMenuOpen = (event: any) => {
		setMobileMoreAnchorEl(event.currentTarget);
	};

	const menuId = 'primary-search-account-menu';
	const renderMenu = (
		<Menu
			anchorEl={anchorEl}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			id={menuId}
			keepMounted
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			open={isMenuOpen}
			onClose={handleMenuClose}
		>
			<MenuItem onClick={handleMenuClose}>Profile</MenuItem>
			<MenuItem onClick={handleMenuClose}>My account</MenuItem>
		</Menu>
	);

	const mobileMenuId = 'primary-search-account-menu-mobile';
	const renderMobileMenu = (
		<Menu
			anchorEl={mobileMoreAnchorEl}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			id={mobileMenuId}
			keepMounted
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			open={isMobileMenuOpen}
			onClose={handleMobileMenuClose}
		>
			<MenuItem>
				<IconButton size="large" aria-label="show 4 new mails" color="inherit">
					<Badge badgeContent={4} color="error">
						<MailIcon />
					</Badge>
				</IconButton>
				<p>Messages</p>
			</MenuItem>
			<MenuItem>
				<IconButton
					size="large"
					aria-label="show 17 new notifications"
					color="inherit"
				>
					<Badge badgeContent={17} color="error">
						<NotificationsIcon />
					</Badge>
				</IconButton>
				<p>Notifications</p>
			</MenuItem>
			<MenuItem onClick={handleProfileMenuOpen}>
				<IconButton
					size="large"
					aria-label="account of current user"
					aria-controls="primary-search-account-menu"
					aria-haspopup="true"
					color="inherit"
				>
					<AccountCircle />
				</IconButton>
				<p>Profile</p>
			</MenuItem>
		</Menu>
	);

	const flyToPoint = (pointInfo: any) => {
		console.log(pointInfo);
		lastSearchedPointRef.current = pointInfo;
		if (pointInfo !== null) {
			if (pointInfo?.bbox) {
				map.flyToBounds([
					[pointInfo.bbox[1], pointInfo.bbox[0]],
					[pointInfo.bbox[3], pointInfo.bbox[2]],
				]);
			} else {
				map.flyTo([pointInfo.lat, pointInfo.lon], 13);
			}
			setPoint(
				<Marker
					position={latLng(pointInfo.lat, pointInfo.lon)}
					eventHandlers={{
						click: (e) => {
							console.log('marker clicked', e);
						},
					}}
				>
					<Popup>
						A pretty CSS3 popup. <br /> Easily customizable.
					</Popup>
				</Marker>
			);
		}

		// map.flyTo([value.lat, value.lon], 11);
	};

	// const expandSearchBox = () => {
	// 	setIsSearchBoxFocused(true);
	// };

	return (
		<Box sx={{ flexGrow: 1 }}>
			{/* <AppBar position="static"> */}
			<Toolbar>
				<IconButton
					size="large"
					edge="start"
					color="inherit"
					aria-label="open drawer"
					sx={{
						mr: 2,
						display: { xs: isSearchBoxFocused ? 'none' : 'block', sm: 'block' },
						right: { xs: isSearchBoxFocused ? '-48px' : '', sm: '' },
						transition: 'display 1s ease-in-out',
					}}
				>
					<MenuIcon />
				</IconButton>
				<Typography
					variant="h6"
					noWrap
					component="div"
					sx={{ display: { xs: 'none', sm: 'block' } }}
				>
					MUI
				</Typography>

				{/* <ComboBox /> */}

				{/* <Box> */}
				<SearchBox
					onFocus={() => {
						setActivePark(null);
						setIsSearchBoxFocused(true);
					}}
					onBlur={() => {
						setIsSearchBoxFocused(false);
					}}
					onClick={() => {
						flyToPoint(lastSearchedPointRef.current);
						// if (searchInput === lastSearchedPointRef?.current?.formatted) {
						// }
						console.log(lastSearchedPointRef?.current?.formatted);

						console.log(searchInput);
					}}
					onChange={(e: any, value: any) => {
						flyToPoint(value); // the value contains the pointInfo Object
					}}
					sxAutoComplete={{
						width: 420,
					}}
					sx={{
						boxShadow: 'none',
						p: '2px 4px',
						display: 'flex',
						alignItems: 'center',
						width: {
							xs: isSearchBoxFocused ? '100%' : '100%',
							sm: '240px',
						},
						transition: 'width 0.4s ease 0s',
					}}
				/>
				{/* </Box> */}
				<Button onClick={() => console.log(map.getZoom())}>סנן</Button>

				<Box sx={{ flexGrow: 1 }} />
				<Box sx={{ display: { xs: 'none', md: 'flex' } }}>
					<IconButton
						size="large"
						aria-label="show 4 new mails"
						color="inherit"
					>
						<Badge badgeContent={4} color="error">
							<MailIcon />
						</Badge>
					</IconButton>
					<IconButton
						size="large"
						aria-label="show 17 new notifications"
						color="inherit"
					>
						<Badge badgeContent={17} color="error">
							<NotificationsIcon />
						</Badge>
					</IconButton>
					<IconButton
						size="large"
						edge="end"
						aria-label="account of current user"
						aria-controls={menuId}
						aria-haspopup="true"
						onClick={handleProfileMenuOpen}
						color="inherit"
					>
						<AccountCircle />
					</IconButton>
				</Box>
				<Box
					sx={{
						display: { xs: isSearchBoxFocused ? 'none' : 'block', sm: 'block' },
					}}
				>
					<IconButton
						size="large"
						aria-label="show more"
						aria-controls={mobileMenuId}
						aria-haspopup="true"
						onClick={handleMobileMenuOpen}
						color="inherit"
					>
						<MoreIcon />
					</IconButton>
				</Box>
			</Toolbar>
			{/* </AppBar> */}
			{renderMobileMenu}
			{renderMenu}
			{/* <TextField
				value={searchInput}
				onChange={(e) => {
					//debugger;
					console.log(e.target.value);
					setSearchInput(e.target.value);
					console.log(searchInput);
				}}
				//	{...params}
				placeholder="חיפוש..."
			/> */}
		</Box>
	);
}
